import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization2 } from '../../contexts/OrganizationContext2';
import {
  disconnectOrganizationIntegration,
  fetchIntegrationStatus,
  startBitlyConnection,
} from './integrationFunctions';

const ADMIN_ROLES = new Set([
  'owner',
  'admin',
  'superAdmin',
  'super_app_admin',
  'superAppadmin',
]);

function getCallableErrorMessage(err) {
  const code = err?.code || '';
  const message = err?.message || '';

  if (code.includes('permission-denied')) {
    return (
      'You do not have permission to manage integrations for this ' +
      'organization.'
    );
  }

  if (code.includes('unauthenticated')) {
    return 'Please sign in again before managing Bitly.';
  }

  return message || 'Failed to manage the Bitly connection.';
}

export default function BitlyDetailModal({
  open,
  onClose,
  onChanged,
}) {
  const {
    hasPermission,
    userProfile,
    userRole,
  } = useAuth();

  const { organization } = useOrganization2();

  const [checking, setChecking] = useState(false);
  const [integration, setIntegration] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);

  const canManageIntegrations = useMemo(() => {
    const role = userRole || userProfile?.role || '';

    return Boolean(
      userProfile?.isOwner ||
        ADMIN_ROLES.has(role) ||
        hasPermission?.('manageIntegrations') ||
        hasPermission?.('manageOrganization')
    );
  }, [hasPermission, userProfile, userRole]);

  const isConnected = integration?.status === 'connected';
  const providerLogin = integration?.providerAccount?.login || null;
  const lastUpdatedAt = integration?.updatedAt || null;

  const refreshStatus = useCallback(async () => {
    if (!organization?.id) {
      setIntegration(null);
      return;
    }

    setChecking(true);
    setErrorMsg('');

    try {
      const status = await fetchIntegrationStatus({
        organizationId: organization.id,
        provider: 'bitly',
      });

      setIntegration(status);
    } catch (err) {
      console.error('[BitlyDetailModal] Status check failed:', err);
      setErrorMsg(getCallableErrorMessage(err));
    } finally {
      setChecking(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    if (!open) return;
    refreshStatus();
  }, [open, refreshStatus]);

  const handleConnectClick = async () => {
    setErrorMsg('');

    if (!organization?.id) {
      setErrorMsg('No organization found. Please try again.');
      return;
    }

    setConnecting(true);

    try {
      const returnUrl = `${window.location.origin}/integrations`;

      const response = await startBitlyConnection({
        organizationId: organization.id,
        returnUrl,
      });

      const authorizationUrl = response?.authorizationUrl;

      if (!authorizationUrl) {
        throw new Error('Missing Bitly authorization URL.');
      }

      window.location.assign(authorizationUrl);
    } catch (err) {
      console.error('[BitlyDetailModal] Connect failed:', err);
      setErrorMsg(getCallableErrorMessage(err));
      setConnecting(false);
    }
  };

  const handleDisconnectClick = async () => {
    setErrorMsg('');

    if (!organization?.id) {
      setErrorMsg('No organization found. Please try again.');
      return;
    }

    setDisconnecting(true);

    try {
      const status = await disconnectOrganizationIntegration({
        organizationId: organization.id,
        provider: 'bitly',
      });

      setIntegration(status);
      setConfirmDisconnectOpen(false);
      onChanged?.();
    } catch (err) {
      console.error('[BitlyDetailModal] Disconnect failed:', err);
      setErrorMsg(getCallableErrorMessage(err));
    } finally {
      setDisconnecting(false);
    }
  };

  if (checking) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Bitly Integration</DialogTitle>
        <DialogContent dividers>
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Checking Bitly connection status...
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <LinkIcon />
            </Avatar>

            <Box>
              <Typography variant="h6" component="div">
                Bitly Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Organization-level shortlink provider
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5}>
            {errorMsg && (
              <Alert severity="error">
                {errorMsg}
              </Alert>
            )}

            {isConnected ? (
              <Alert severity="success">
                Bitly is connected
                {providerLogin ? ` as ${providerLogin}` : ''}.
              </Alert>
            ) : (
              <Alert severity="info">
                Bitly is not connected for this organization.
              </Alert>
            )}

            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Connect your organization&apos;s Bitly account so generated
                campaign URLs can be shortened through your team&apos;s
                Bitly workspace.
              </Typography>

              <Typography variant="body2" color="text.secondary">
                This connection is managed centrally. Forms can opt into
                using Bitly later, but public form users will never see or
                manage your organization&apos;s Bitly credentials.
              </Typography>
            </Box>

            <Divider />

            <Stack spacing={1}>
              <Typography variant="subtitle2">
                Connection details
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  size="small"
                  label={isConnected ? 'Connected' : 'Not installed'}
                  color={isConnected ? 'success' : 'default'}
                  variant={isConnected ? 'filled' : 'outlined'}
                />

                {providerLogin && (
                  <Chip
                    size="small"
                    label={`Account: ${providerLogin}`}
                    variant="outlined"
                  />
                )}
              </Stack>

              {lastUpdatedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last updated: {lastUpdatedAt}
                </Typography>
              )}
            </Stack>

            {!canManageIntegrations && (
              <Alert severity="warning">
                You can view this integration, but only an organization
                owner or admin can connect, reconnect, or disconnect Bitly.
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>
            Close
          </Button>

          {isConnected && (
            <Button
              onClick={() => setConfirmDisconnectOpen(true)}
              disabled={!canManageIntegrations || disconnecting}
              color="error"
              startIcon={<PowerSettingsNewIcon />}
            >
              Disconnect
            </Button>
          )}

          <Button
            onClick={handleConnectClick}
            variant="contained"
            disabled={connecting || !canManageIntegrations}
            endIcon={<OpenInNewIcon />}
          >
            {connecting
              ? 'Connecting...'
              : isConnected
                ? 'Reconnect Bitly'
                : 'Connect Bitly'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDisconnectOpen}
        onClose={() => setConfirmDisconnectOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Disconnect Bitly?
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            This removes the organization&apos;s stored Bitly credential.
            Existing generated links are not deleted, but forms will not be
            able to create new Bitly shortlinks until Bitly is reconnected.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setConfirmDisconnectOpen(false)}
            disabled={disconnecting}
          >
            Cancel
          </Button>

          <Button
            onClick={handleDisconnectClick}
            color="error"
            variant="contained"
            disabled={disconnecting}
          >
            {disconnecting ? 'Disconnecting...' : 'Disconnect Bitly'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
